/**
 * Which ticket states can follow which (§4.1, FR-LC-01).
 *
 * IN `shared` RATHER THAN `core` BECAUSE THE BROWSER NEEDS IT. A queue board
 * that offers a column the server will refuse, or a menu listing a transition
 * that cannot happen, is a button whose only function is to produce an error
 * — so the UI has to know the table too.
 *
 * `core` re-exports these, so the server keeps importing them from where it
 * always did. The table lives in one place either way: two copies of "what
 * may follow what" is the kind of duplication that drifts silently and is
 * only noticed when a legal move starts being rejected.
 *
 * The authority is still the server. This makes the UI agree with it; it does
 * not make the UI the one deciding.
 */
import type { TicketState } from './types.js';

/** Legal transitions. Absent target = illegal. */
const TRANSITIONS: Readonly<Record<TicketState, readonly TicketState[]>> = {
  new: ['open', 'pending_requester', 'pending_external', 'pending_internal', 'pending_event', 'resolved', 'cancelled'],
  open: ['pending_requester', 'pending_external', 'pending_internal', 'pending_event', 'resolved', 'cancelled'],
  // A pending ticket can move directly to another kind of pending: "waiting on
  // the requester" becoming "waiting on a vendor" is a real and common change,
  // and forcing a detour through `open` would corrupt the blocked-time report.
  pending_requester: ['open', 'pending_external', 'pending_internal', 'pending_event', 'resolved', 'cancelled'],
  pending_external: ['open', 'pending_requester', 'pending_internal', 'pending_event', 'resolved', 'cancelled'],
  pending_internal: ['open', 'pending_requester', 'pending_external', 'pending_event', 'resolved', 'cancelled'],
  // A parked ticket can still be answered, cancelled or re-parked before its
  // date arrives — the wait is a plan, not a cage. Leaving it clears the
  // event; only the sweep firing sets `event_fired_at`.
  pending_event: ['open', 'pending_requester', 'pending_external', 'pending_internal', 'resolved', 'cancelled'],
  // FR-LC-03: reopening within the window returns the ORIGINAL ticket to open.
  resolved: ['open', 'closed', 'cancelled'],
  // Terminal. Both by design: a closed ticket that can reopen makes "closed"
  // meaningless for reporting, so a late follow-up opens a new linked ticket.
  closed: [],
  cancelled: [],
};

export function legalTransitionsFrom(state: TicketState): readonly TicketState[] {
  return TRANSITIONS[state];
}

export function canTransition(from: TicketState, to: TicketState): boolean {
  return TRANSITIONS[from].includes(to);
}
